import {
  RESET_VALIDATION_ERRORS,
  SET_VALIDATION_ERRORS,
  VALIDATE_FIELDS_SUCCESS,
} from 'constants/fields';
import { REFUND_KYC_FIELDS_MISSING } from 'constants/kycs';
import {
  CREATE_ORDER_FAILURE,
  CREATE_ORDER_SUCCESS,
  UPDATE_ORDER_FAILURE,
  UPDATE_ORDER_SUCCESS,
} from 'constants/orders';
import {
  type UPDATE_PAYMENT_FAILURE,
  UPDATE_PAYMENT_SUCCESS,
} from 'constants/payment';
import {
  CLEAR_FIELD_ERROR,
  GO_TO_PREV_STEP,
  GO_TO_STEP,
  RESTART,
  SET_FIELD_VALUE,
  SET_LOCALE,
} from 'constants/ui';
import type { ApiError } from 'models/error';
import { combineReducers } from 'redux';

type ErrorsAction =
  | {
      type: typeof VALIDATE_FIELDS_SUCCESS;
      payload: {
        errorParams: string[];
        errors: Record<string, ApiError>;
      };
    }
  | {
      type: typeof SET_FIELD_VALUE;
      payload: {
        name: string;
        value: string;
      };
    }
  | {
      type: typeof CLEAR_FIELD_ERROR;
      payload: {
        fieldId: string;
      };
    }
  | {
      type:
        | typeof CREATE_ORDER_FAILURE
        | typeof UPDATE_ORDER_FAILURE
        | typeof UPDATE_PAYMENT_FAILURE;
      payload: {
        entities: {
          errors: Record<string, ApiError>;
        };
        result: string[];
      };
    }
  | {
      type: typeof REFUND_KYC_FIELDS_MISSING;
      payload: {
        fields: string[];
      };
    }
  | {
      type: typeof SET_VALIDATION_ERRORS;
      payload: {
        [key: string]: string[];
      };
    }
  | {
      type:
        | typeof CREATE_ORDER_SUCCESS
        | typeof GO_TO_PREV_STEP
        | typeof GO_TO_STEP
        | typeof RESET_VALIDATION_ERRORS
        | typeof RESTART
        | typeof SET_LOCALE
        | typeof UPDATE_ORDER_SUCCESS
        | typeof UPDATE_PAYMENT_SUCCESS;
    };

export const byParam = (
  state: Record<string, ApiError> = {},
  action: ErrorsAction,
) => {
  const newState = { ...state };

  switch (action.type) {
    case SET_FIELD_VALUE:
      delete newState[action.payload.name];
      return newState;
    case CLEAR_FIELD_ERROR:
      delete newState[action.payload.fieldId];
      return newState;
    case VALIDATE_FIELDS_SUCCESS:
      return { ...state, ...action.payload.errors };
    case CREATE_ORDER_FAILURE:
    case UPDATE_ORDER_FAILURE:
      const { entities: { errors } = {} } = action.payload;
      return { ...state, ...errors };
    case SET_VALIDATION_ERRORS:
      Object.keys(action.payload).forEach(name => {
        if (action.payload[name].length === 0) {
          delete newState[name];
        } else {
          newState[name] = {
            message: action.payload[name][0],
            param: name,
            type: 'invalid_param',
          };
        }
      });
      return newState;
    case CREATE_ORDER_SUCCESS:
    case RESTART:
    case RESET_VALIDATION_ERRORS:
    case SET_LOCALE:
    case GO_TO_PREV_STEP:
    case GO_TO_STEP:
    case UPDATE_ORDER_SUCCESS:
    case UPDATE_PAYMENT_SUCCESS:
      return {};
    default:
      return state;
  }
};

export const updateParamsFromValidationErrors = (
  state: string[],
  payload: Record<string, string[]>,
): string[] => {
  const newState = [...state];

  // removes fields from state if they are not in the payload and adds fields to state if they are in the payload
  Object.keys(payload).forEach(name => {
    if (payload[name].length === 0) {
      const position = newState.indexOf(name);
      if (position !== -1) {
        newState.splice(position, 1);
      }
    } else if (newState.indexOf(name) === -1) {
      newState.push(name);
    }
  });

  return newState;
};

export const params = (state: string[] = [], action: ErrorsAction) => {
  let newState: string[];

  switch (action.type) {
    case SET_FIELD_VALUE:
      newState = [...state];
      const position = newState.indexOf(action.payload.name);
      if (position !== -1) {
        newState.splice(position, 1);
      }
      return newState;
    case CLEAR_FIELD_ERROR:
      newState = [...state];
      const fieldPosition = newState.indexOf(action.payload.fieldId);
      if (fieldPosition !== -1) {
        newState.splice(fieldPosition, 1);
      }
      return newState;
    case CREATE_ORDER_FAILURE:
    case UPDATE_ORDER_FAILURE:
      const { result = [] } = action.payload;
      return [...new Set([...state, ...result])];
    case SET_VALIDATION_ERRORS:
      return updateParamsFromValidationErrors(state, action.payload);
    case VALIDATE_FIELDS_SUCCESS:
      return [...state, ...action.payload.errorParams];
    case REFUND_KYC_FIELDS_MISSING:
      return action.payload.fields;
    case CREATE_ORDER_SUCCESS:
    case RESTART:
    case RESET_VALIDATION_ERRORS:
    case SET_LOCALE:
    case GO_TO_PREV_STEP:
    case GO_TO_STEP:
    case UPDATE_ORDER_SUCCESS:
    case UPDATE_PAYMENT_SUCCESS:
      return [];
    default:
      return state;
  }
};

const errors = combineReducers({
  byParam,
  params,
});

export { errors };
